import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocale } from "@genius-tracker/i18n/react";
import { logout, type SessionInfo } from "../lib/api.js";
import { usePlatformOwner } from "../access/platform-owner.js";

/**
 * Sprint 24 Wave B PR-B2 — Admin Console account menu + sign-out.
 * Sprint 24.1 Hotfix PR-H1 — viewport-aware popover positioning and
 * admin-safe logout redirect.
 *
 * The Sprint 23 staging smoke surfaced that the Admin Console had no
 * account menu and no logout button: `apps/admin/src/lib/api.ts:126`
 * exported a `logout()` helper that posted to `/v1/auth/logout` and
 * returned the Keycloak `endSessionUrl`, but no UI was wired to it,
 * so the operator could not switch identities without DevTools.
 *
 * This component exposes the existing `logout()` from a small popover
 * in the existing header. It deliberately stays in the same shell
 * (`apps/admin/src/AdminApp.tsx`) — no new `<AppShell>` wrapper, no
 * new `<Routes>` block. Identity comes from the existing `SessionInfo`
 * passed from the caller (the same shape returned by `useSession()`),
 * so the component is trivially testable without a SessionProvider.
 *
 * Sprint 24.1 PR-H1 fixes:
 *
 *   1. **Popover positioning** — the original `position: absolute;
 *      inset-inline-end: 0` worked when the trigger button was near
 *      the right edge of the viewport (its anchor) but rendered the
 *      menu outside the viewport on narrow / mobile layouts where the
 *      trigger has shifted. The new positioning measures the trigger
 *      via `getBoundingClientRect()` and positions the menu with
 *      `position: fixed`, clamped to the viewport with an 8 px safe
 *      gutter. The menu width also caps at viewport-minus-gutter so
 *      it never grows past the screen.
 *
 *   2. **Admin-safe logout** — sign-out now passes
 *      `${window.location.origin}/admin/` as `returnTo` to the
 *      `/v1/auth/logout` endpoint. The server validates this against
 *      the configured `postLoginRedirectBases` (origin-match — same
 *      allow-list the login flow already uses) and threads the URL
 *      to Keycloak's RP-Initiated Logout. The operator returns to
 *      the Admin Console login flow instead of dropping on the Main
 *      App. No new env var. No Keycloak realm change (same origin is
 *      already allow-listed by virtue of being the login base).
 *
 * Safety:
 *   - Renders only `identity.displayName` and `identity.email` from
 *     the session. Never renders raw JWT, cookie, refresh token, or
 *     `sub`. Role badges are derived from the public `roles` claim
 *     via the existing `isPlatformOwner` helper.
 *   - "Sign out" calls the existing `logout()` API helper and
 *     assigns the Keycloak `endSessionUrl`. No call to Keycloak
 *     `sendVerifyEmail` (which triggers realm SMTP — held in
 *     Sprint 24 per `KEYCLOAK_PROVISIONING_STRATEGY.md` §2a).
 *   - "Switch account" reuses the same logout-then-login flow so the
 *     operator returns to the Keycloak login page and can authenticate
 *     as a different user. No impersonation, no support mode.
 *
 * Accessibility:
 *   - Trigger has `aria-haspopup="menu"` and `aria-expanded`.
 *   - Popover has `role="menu"` and the actions have `role="menuitem"`.
 *   - Escape closes the popover.
 *   - Click outside closes the popover.
 */

interface AccountMenuProps {
  /**
   * Current authenticated session. Pass the value returned by
   * `useSession()`.
   */
  session: SessionInfo;
  /**
   * Sprint 55.3 — optional callback fired when the operator clicks
   * the "Profile" link. Wires the admin nav back to the page.
   */
  onProfileClick?: () => void;
  /**
   * Optional logout-handler injection point for tests so the unit
   * test does not have to stub global `fetch` and `window.location`.
   * Production callers omit it.
   *
   * Sprint 24.1 PR-H1 — receives the `returnTo` URL the menu wants
   * Keycloak to redirect to after end-session (i.e. the admin path
   * on the same origin).
   */
  onSignOut?: (returnTo?: string) => Promise<string>;
  /**
   * Optional navigation injection point for tests. Production
   * callers omit it; we default to `window.location.assign`.
   */
  onNavigate?: (url: string) => void;
  /**
   * Optional override for the post-logout return URL. Tests pass an
   * explicit value so they don't depend on JSDOM's `window.location`
   * shape. Production callers omit it; we default to
   * `${window.location.origin}/admin/`.
   */
  postLogoutReturnTo?: string;
}

interface MenuPosition {
  top: number;
  left: number;
  maxInlineSize: number;
}

/**
 * Compute a viewport-clamped position for the popover, anchored under
 * the trigger and aligned to its right edge by default. Pure function
 * so it is unit-testable without a real DOM.
 */
export function computeMenuPosition(
  triggerRect: { top: number; bottom: number; left: number; right: number },
  viewport: { width: number; height: number },
  preferredInlineSize = 280,
): MenuPosition {
  const GUTTER = 8;
  const VERTICAL_GAP = 6;
  const maxInlineSize = Math.max(160, Math.min(preferredInlineSize, viewport.width - GUTTER * 2));

  // Default: anchor menu's right edge to trigger's right edge.
  let left = triggerRect.right - maxInlineSize;
  // Clamp into viewport: ≥ GUTTER on the left, ≤ width-maxInlineSize-GUTTER on the right.
  if (left < GUTTER) left = GUTTER;
  if (left + maxInlineSize > viewport.width - GUTTER) {
    left = Math.max(GUTTER, viewport.width - maxInlineSize - GUTTER);
  }

  // Default: drop below the trigger.
  let top = triggerRect.bottom + VERTICAL_GAP;
  // If overflowing bottom, place above the trigger if there's room.
  // (We don't know the menu's height ahead of time; use a conservative
  // 320px estimate and only flip if the trigger is in the bottom half.)
  const ESTIMATED_HEIGHT = 320;
  if (
    top + ESTIMATED_HEIGHT > viewport.height - GUTTER &&
    triggerRect.top - VERTICAL_GAP - ESTIMATED_HEIGHT > GUTTER
  ) {
    top = triggerRect.top - VERTICAL_GAP - ESTIMATED_HEIGHT;
  }
  // Final clamp so the top edge is always visible.
  if (top < GUTTER) top = GUTTER;

  return { top, left, maxInlineSize };
}

export function AccountMenu({
  session,
  onSignOut,
  onNavigate,
  postLogoutReturnTo,
  onProfileClick,
}: AccountMenuProps) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onClick = (e: MouseEvent) => {
      const node = containerRef.current;
      if (!node) return;
      if (!node.contains(e.target as Node)) close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return;
    }
    const recompute = () => {
      const node = triggerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setPosition(
        computeMenuPosition(rect, {
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  const handleSignOut = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const fn = onSignOut ?? logout;
      // Sprint 25 Wave A PR-A1 — replace the Sprint 24.1 PR-H1
      // "send `/admin/` as KC `post_logout_redirect_uri`" approach.
      // Operator finding on staging: Keycloak rejects that URL with
      // "Invalid redirect uri" because the realm client allow-list
      // only contains `https://devin.ahnyne.com/` — the `/admin/`
      // path is not allow-listed. Sprint 25 Wave A explicitly
      // forbids changing Keycloak realm config, so we cannot fix
      // the rejection at KC's side.
      //
      // Workaround: do NOT pass a custom `returnTo` to the logout
      // route. The server falls back to its existing
      // `postLogoutRedirectUrl` (= the first base in
      // `OIDC_POST_LOGIN_REDIRECT_BASES`, which is allow-listed by
      // construction). Before navigating to KC, drop a short-lived
      // marker in `localStorage` so the customer-app root can
      // bounce the operator back to `/admin/` after KC's redirect.
      // This works without any KC config change because the actual
      // `post_logout_redirect_uri` we send to KC is unchanged from
      // pre-Sprint-24.1 behavior; only the *post-redirect handling*
      // changes, and that's purely client-side.
      const target =
        postLogoutReturnTo ??
        (typeof window !== "undefined" ? `${window.location.origin}/admin/` : null);
      if (target && typeof window !== "undefined") {
        try {
          window.localStorage.setItem("gt:post-logout-target", target);
        } catch {
          // localStorage can be unavailable (private mode / locked
          // storage). The fallback is just landing on the main app
          // post-logout — better UX than KC's "Invalid redirect uri".
        }
      }
      // Pass `undefined` to logout() so the server uses the
      // pre-existing default. This is the fix that prevents the
      // "Invalid redirect uri" KC rejection on staging.
      const endSessionUrl = await fn(undefined);
      const navigate = onNavigate ?? ((u: string) => window.location.assign(u));
      navigate(endSessionUrl);
    } catch (err) {
      // Reset busy so the operator can retry.
      setBusy(false);
      // Sprint 25 PR-A1 follow-up (Codex P2) — clear the bounce
      // marker on sign-out failure. We wrote it BEFORE calling
      // logout() so KC's end-session would land back on `/admin/`,
      // but if the logout request itself fails the navigation
      // never happens and the marker persists. Next page load
      // (operator clicks something unrelated, or refresh) would
      // then spuriously redirect to `/admin/`. Clear it now to keep
      // the bounce strictly tied to a successful logout.
      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem("gt:post-logout-target");
        } catch {
          /* ignore */
        }
      }
      // Visible error stays inside the menu — never include any token
      // body / cookie / status detail beyond the localized string.
      console.error("admin.account.signout.failed", err instanceof Error ? err.message : "");
    }
  }, [busy, onSignOut, onNavigate, postLogoutReturnTo]);

  const platformOwner = usePlatformOwner(session);
  const display = session.identity.displayName?.trim() || session.identity.email;
  const initials = computeInitials(session.identity.displayName, session.identity.email);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("admin.account.menu_label")}
        title={display}
        style={triggerStyle}
      >
        <span aria-hidden="true" style={avatarStyle}>
          {initials}
        </span>
      </button>
      {open ? (
        <div
          role="menu"
          aria-label={t("admin.account.menu_label")}
          data-testid="admin-account-menu"
          style={{
            ...menuStyle,
            ...(position
              ? {
                  // Sprint 24.1 PR-H1 follow-up: use **physical**
                  // `top` / `left` here, NOT logical
                  // `insetBlockStart` / `insetInlineStart`. The
                  // coordinates returned by `getBoundingClientRect()`
                  // and `window.innerWidth` are physical viewport
                  // coordinates regardless of writing direction; in
                  // RTL locales (ar) `insetInlineStart` maps to the
                  // right edge and would mirror the menu away from
                  // the trigger and possibly off-screen.
                  top: position.top,
                  left: position.left,
                  maxInlineSize: position.maxInlineSize,
                }
              : { visibility: "hidden" }),
          }}
        >
          <div style={identitySectionStyle}>
            <div style={signedInLabelStyle}>{t("admin.account.signed_in_as")}</div>
            <div style={emailStyle}>{session.identity.email}</div>
            {session.identity.displayName ? (
              <div style={displayNameStyle}>{session.identity.displayName}</div>
            ) : null}
            {platformOwner ? (
              <div style={roleBadgeStyle} aria-label={t("admin.account.role.platform_owner")}>
                {t("admin.account.role.platform_owner")}
              </div>
            ) : null}
          </div>
          <div style={dividerStyle} aria-hidden="true" />
          {onProfileClick ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onProfileClick();
              }}
              style={actionStyle}
              data-testid="admin-account-menu-profile"
            >
              {t("admin.account.profile")}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            disabled={busy}
            style={actionStyle}
          >
            {t("admin.account.switch_account")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            disabled={busy}
            style={{ ...actionStyle, color: "var(--gt-sys-danger, #c0392b)" }}
          >
            {t("admin.account.sign_out")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function computeInitials(displayName: string | null, email: string): string {
  const source = displayName?.trim() || email;
  if (!source) return "?";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (!first) return source.charAt(0).toUpperCase();
  if (!second) return first.slice(0, 2).toUpperCase();
  return ((first.charAt(0) ?? "") + (second.charAt(0) ?? "")).toUpperCase();
}

const triggerStyle: React.CSSProperties = {
  appearance: "none",
  border: "1px solid var(--gt-sys-border)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-1)",
  borderRadius: "var(--gt-sys-radius-pill, 999px)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const avatarStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  inlineSize: 28,
  blockSize: 28,
  borderRadius: "50%",
  background: "color-mix(in oklab, var(--gt-sys-accent) 14%, transparent)",
  color: "var(--gt-sys-accent)",
  font: "600 12px/1 system-ui, sans-serif",
  letterSpacing: 0.5,
};

/**
 * Sprint 24.1 PR-H1 — `position: fixed` so the popover is positioned
 * relative to the viewport (not the trigger's offset parent). The
 * actual `top` / `left` / `maxInlineSize` are injected at render time
 * by `computeMenuPosition`. This means the popover is guaranteed to
 * stay inside the viewport on both desktop and mobile, including
 * narrow phone widths where the trigger may have shifted.
 */
const menuStyle: React.CSSProperties = {
  position: "fixed",
  zIndex: 20,
  minInlineSize: 240,
  display: "flex",
  flexDirection: "column",
  background: "var(--gt-sys-surface)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-md)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  overflow: "hidden",
};

const identitySectionStyle: React.CSSProperties = {
  paddingBlock: "var(--gt-sys-space-3)",
  paddingInline: "var(--gt-sys-space-3)",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const signedInLabelStyle: React.CSSProperties = {
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text-muted)",
  textTransform: "uppercase",
  letterSpacing: 0.04,
};

const emailStyle: React.CSSProperties = {
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text)",
  wordBreak: "break-all",
};

const displayNameStyle: React.CSSProperties = {
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text-muted)",
};

const roleBadgeStyle: React.CSSProperties = {
  marginBlockStart: 6,
  alignSelf: "flex-start",
  paddingBlock: 2,
  paddingInline: "var(--gt-sys-space-2)",
  borderRadius: "var(--gt-sys-radius-pill, 999px)",
  background: "color-mix(in oklab, var(--gt-sys-accent) 14%, transparent)",
  color: "var(--gt-sys-accent)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: 600,
};

const dividerStyle: React.CSSProperties = {
  blockSize: 1,
  background: "var(--gt-sys-border)",
};

const actionStyle: React.CSSProperties = {
  appearance: "none",
  border: "none",
  background: "transparent",
  color: "var(--gt-sys-text)",
  textAlign: "start",
  paddingBlock: "var(--gt-sys-space-2)",
  paddingInline: "var(--gt-sys-space-3)",
  cursor: "pointer",
  font: "inherit",
};
